import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServices } from "@/app/use-services";
import { errorMessage } from "@/lib/api/errors";
import type { MemoryFact } from "@/lib/api/models/consult";
import { BackLink } from "@/ui/BackLink";
import { ErrorView } from "@/ui/ErrorView";
import { Page } from "@/ui/Page";
import { Body, Display, Label } from "@/ui/Text";
import { MEMORY_QUERY_KEY } from "./keys";

/** Everything she remembers, one fact per hairline row, each removable. */
export function MemoryPage() {
  const { api } = useServices();
  const client = useQueryClient();
  const facts = useQuery({ queryKey: MEMORY_QUERY_KEY, queryFn: () => api.fetchMemory() });
  const forget = useMutation({
    mutationFn: (id: number) => api.forgetMemory(id),
    onMutate: async (id) => {
      await client.cancelQueries({ queryKey: MEMORY_QUERY_KEY });
      const previous = client.getQueryData<readonly MemoryFact[]>(MEMORY_QUERY_KEY);
      client.setQueryData<readonly MemoryFact[]>(MEMORY_QUERY_KEY, (old) => old?.filter((f) => f.id !== id) ?? []);
      return { previous };
    },
    onError: (_error, _id, context) => {
      // A forget that failed must not look like one that worked.
      if (context?.previous) client.setQueryData(MEMORY_QUERY_KEY, context.previous);
    },
    onSettled: () => void client.invalidateQueries({ queryKey: MEMORY_QUERY_KEY }),
  });

  return (
    <Page>
      <BackLink to="/you" label="You" />
      <Label className="mt-3">What she remembers</Label>
      {facts.isPending && <Body className="mt-3">Reading…</Body>}
      {facts.isError && <ErrorView message={errorMessage(facts.error)} onRetry={() => void facts.refetch()} />}
      {facts.data && facts.data.length === 0 && (
        <>
          <Display className="mt-3">Nothing yet.</Display>
          <Body className="mt-3">As you talk, she’ll keep track of the things that matter — decisions you’re weighing, what’s going on at work, who’s on your mind. It all shows up here, and you can remove any of it.</Body>
        </>
      )}
      {facts.data && facts.data.length > 0 && (
        <>
          <Body className="mt-3">Everything below is hers to work from. Remove anything you would rather she forgot.</Body>
          <ul className="mt-4">
            {facts.data.map((fact) => (
              <li key={fact.id} className="flex items-start gap-3 border-b border-rule py-3">
                <span className="flex-1 text-[16px] leading-[1.45]">{fact.fact}</span>
                <button
                  type="button"
                  aria-label={`Forget: ${fact.fact}`}
                  onClick={() => { if (window.confirm("Forget this?")) forget.mutate(fact.id); }}
                  className="min-h-11 min-w-11 text-[18px] text-mid"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          {forget.isError && <Body className="mt-3">{errorMessage(forget.error)}</Body>}
        </>
      )}
    </Page>
  );
}
